import { z } from 'zod';
import { ClientCommandIdSchema } from './client-command-id';
import { SlideStatusSchema } from './slide-status';

// Wire shapes for the versioning feature, shared by decks and workbooks.
// The feed is a flat, payload-free op-metadata page plus sidecar collections;
// grouping into timeline entries happens client-side.

export const VersionFeedOperationSchema = z.object({
  id: z.string(),
  sequence: z.number().int().nonnegative(),
  kind: z.string(),
  source: z.string(),
  historyAction: z.string(),
  groupId: z.string().nullable(),
  actorUserId: z.string().nullable(),
  actorRunId: z.string().nullable(),
  affectedIds: z.array(z.string()),
  createdAt: z.string(),
});
export type VersionFeedOperation = z.infer<typeof VersionFeedOperationSchema>;

export const VersionFeedRunSchema = z.object({
  runId: z.string(),
  status: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});
export type VersionFeedRun = z.infer<typeof VersionFeedRunSchema>;

export const VersionFeedCheckpointSchema = z.object({
  sequence: z.number().int().nonnegative(),
  reason: z.string(),
  createdAt: z.string(),
});
export type VersionFeedCheckpoint = z.infer<typeof VersionFeedCheckpointSchema>;

export const VersionFeedPublishedVersionSchema = z.object({
  id: z.string(),
  name: z.string(),
  publishedSequence: z.number().int().nonnegative(),
  itemCount: z.number().int().nonnegative(),
  createdById: z.string(),
  updatedById: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type VersionFeedPublishedVersion = z.infer<
  typeof VersionFeedPublishedVersionSchema
>;

export const VersionFeedUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string().nullable(),
});
export type VersionFeedUser = z.infer<typeof VersionFeedUserSchema>;

export const VersionFeedSchema = z.object({
  headSequence: z.number().int().nonnegative(),
  documentCreatedAt: z.string(),
  operations: z.array(VersionFeedOperationSchema),
  runs: z.array(VersionFeedRunSchema),
  checkpoints: z.array(VersionFeedCheckpointSchema),
  publishedVersions: z.array(VersionFeedPublishedVersionSchema),
  users: z.record(z.string(), VersionFeedUserSchema),
  nextCursor: z.number().int().nonnegative().nullable(),
});
export type VersionFeed = z.infer<typeof VersionFeedSchema>;

export const DeckVersionStateSlideSchema = z.object({
  id: z.string(),
  order: z.number().int().nonnegative(),
  html: z.string(),
  notes: z.string().nullable(),
  status: SlideStatusSchema.optional(),
  /** @deprecated Legacy stored snapshots only; normalize via normalizeSlideStatus. */
  isDraft: z.boolean().optional(),
});
export type DeckVersionStateSlide = z.infer<typeof DeckVersionStateSlideSchema>;

export const DeckVersionStateSchema = z.object({
  sequence: z.number().int().nonnegative(),
  name: z.string(),
  outline: z.string().nullable(),
  slides: z.array(DeckVersionStateSlideSchema),
});
export type DeckVersionState = z.infer<typeof DeckVersionStateSchema>;

export const WorkbookVersionStateSheetSchema = z.object({
  id: z.string(),
  order: z.number().int().nonnegative(),
  name: z.string(),
  data: z.unknown(),
});
export type WorkbookVersionStateSheet = z.infer<
  typeof WorkbookVersionStateSheetSchema
>;

export const WorkbookVersionStateSchema = z.object({
  sequence: z.number().int().nonnegative(),
  name: z.string(),
  sheets: z.array(WorkbookVersionStateSheetSchema),
});
export type WorkbookVersionState = z.infer<typeof WorkbookVersionStateSchema>;

// Restore / revert requests. No baseSequence: restores target an absolute
// historical state, so the server reads the head itself and the op-log
// staleness check guards the race.
export const VersionRestoreRequestSchema = z.object({
  sequence: z.number().int().nonnegative(),
  clientId: z.string().min(1),
  clientCommandId: ClientCommandIdSchema,
  /** The restoring browser session, so its Undo can take the restore back. */
  actorSessionId: z.string().min(1).optional(),
});
export type VersionRestoreRequest = z.infer<typeof VersionRestoreRequestSchema>;

export const SlideRestoreRequestSchema = z.object({
  sequence: z.number().int().nonnegative(),
  slideId: z.string().min(1),
  clientId: z.string().min(1),
  clientCommandId: ClientCommandIdSchema,
});
export type SlideRestoreRequest = z.infer<typeof SlideRestoreRequestSchema>;

export const RunRevertRequestSchema = z.object({
  clientId: z.string().min(1),
  clientCommandId: ClientCommandIdSchema,
  /** Apply even when later edits touched the same slides/sheets. */
  force: z.boolean().optional(),
});
export type RunRevertRequest = z.infer<typeof RunRevertRequestSchema>;

export const RunRevertConflictSchema = z.object({
  error: z.literal('revert_conflict'),
  conflictingIds: z.array(z.string()),
});
export type RunRevertConflict = z.infer<typeof RunRevertConflictSchema>;

// Published versions.
export const PublishVersionRequestSchema = z.object({
  name: z.string().min(1).max(120),
});
export type PublishVersionRequest = z.infer<typeof PublishVersionRequestSchema>;

export const PublishedVersionListSchema = z.object({
  versions: z.array(VersionFeedPublishedVersionSchema),
  users: z.record(z.string(), VersionFeedUserSchema),
});
export type PublishedVersionList = z.infer<typeof PublishedVersionListSchema>;

export const PublishedVersionStateResponseSchema = z.object({
  version: VersionFeedPublishedVersionSchema,
  state: z.unknown(),
});
export type PublishedVersionStateResponse = z.infer<
  typeof PublishedVersionStateResponseSchema
>;
